import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, timeout, retry } from 'rxjs';

interface WeatherData {
  temp: number | null;
  description: string;
  icon: string;
}

interface WeatherForecast extends WeatherData {
  date: Date;
}

interface WeatherResponse {
  current: WeatherData;
  forecast: WeatherForecast[];
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  // Coordenadas de Capital Federal
  private readonly LAT = -34.36;
  private readonly LON = -58.22;
  private readonly WEATHER_API_URL = 'https://api.open-meteo.com/v1/forecast';
  private readonly DOLAR_API_URL = 'https://dolarapi.com/v1/dolares';

  constructor(private http: HttpClient) {}

  getWeather(): Observable<WeatherResponse | null> {
    const params = {
      latitude: this.LAT.toString(),
      longitude: this.LON.toString(),
      current: 'temperature_2m,weather_code,is_day',
      daily: 'weather_code,temperature_2m_max,temperature_2m_min',
      timezone: 'America/Argentina/Buenos_Aires',
      forecast_days: '7'
    };

    // Añadimos timestamp para evitar cache del navegador
    const ts = Date.now().toString();
    const weatherParams = { ...params, ts };
    return this.http.get<any>(this.WEATHER_API_URL, { params: weatherParams }).pipe(
      timeout(10000), // 10 segundos de timeout
      retry(2), // Reintentar 2 veces en caso de error
      map((response: any) => {
        // console.log('Respuesta de la API del clima:', response);
        
        // Validar estructura de respuesta
        if (!response || !response.current || !response.daily) {
          throw new Error('Estructura de respuesta inválida');
        }

        // Validar datos actuales
        if (typeof response.current.temperature_2m !== 'number' || 
            typeof response.current.weather_code !== 'number') {
          throw new Error('Datos del clima actual inválidos');
        }

        const current: WeatherData = {
          temp: Math.round(response.current.temperature_2m),
          description: this.getWeatherDescription(response.current.weather_code),
          icon: this.getWeatherIcon(response.current.weather_code, response.current.is_day)
        };

        // Validar datos del pronóstico
        const forecast: WeatherForecast[] = [];
        if (response.daily.time && Array.isArray(response.daily.time)) {
          for (let i = 0; i < Math.min(response.daily.time.length, 7); i++) {
            const date = response.daily.time[i];
            const maxTemp = response.daily.temperature_2m_max?.[i];
            const minTemp = response.daily.temperature_2m_min?.[i];
            const weatherCode = response.daily.weather_code?.[i];

            if (date && typeof maxTemp === 'number' && typeof minTemp === 'number' && typeof weatherCode === 'number') {
              forecast.push({
                date: new Date(date),
                temp: Math.round((maxTemp + minTemp) / 2),
                description: this.getWeatherDescription(weatherCode),
                icon: this.getWeatherIcon(weatherCode, true) // Usar ícono de día para pronóstico
              });
            }
          }
        }

        return { current, forecast };
      }),
      catchError((error: HttpErrorResponse) => {
        // Silenciado en producción; devolver valor nulo en caso de error
        return of(null);
      })
    );
  }

  getDolarRates(): Observable<any> {
    // Añadimos timestamp para evitar cache del navegador
    const ts = Date.now().toString();
    return this.http.get<any>(this.DOLAR_API_URL, { params: { ts } }).pipe(
      timeout(10000),
      retry(2),
      map((response: any) => {
        // console.log('Respuesta de la API del dólar:', response);
        
        // Validar que la respuesta sea un array
        if (!Array.isArray(response)) {
          throw new Error('Respuesta del dólar no es un array');
        }

        // Transformar y validar los datos para que coincidan con la estructura esperada
        const validRates = response.filter(rate => 
          rate && 
          typeof rate.casa === 'string' && 
          (typeof rate.compra === 'number' || typeof rate.venta === 'number')
        ).map(rate => ({
          casa: rate.casa,        // Usar 'casa' que es el identificador único
          nombre: rate.nombre,    // Nombre descriptivo
          compra: rate.compra,
          venta: rate.venta,
          fechaActualizacion: rate.fechaActualizacion
        }));

        if (validRates.length === 0) {
          throw new Error('No hay cotizaciones válidas');
        }

        // console.log('Datos del dólar transformados:', validRates);
        return validRates;
      }),
      catchError((error: HttpErrorResponse) => {
        // Silenciado en producción; devolver valor nulo en caso de error
        return of(null);
      })
    );
  }

  private getWeatherDescription(code: number): string {
    const descriptions: { [key: number]: string } = {
      0: 'Despejado',
      1: 'Mayormente despejado',
      2: 'Parcialmente nublado',
      3: 'Nublado',
      45: 'Neblina',
      48: 'Niebla con escarcha',
      51: 'Llovizna ligera',
      53: 'Llovizna moderada',
      55: 'Llovizna intensa',
      56: 'Llovizna helada ligera',
      57: 'Llovizna helada intensa',
      61: 'Lluvia ligera',
      63: 'Lluvia moderada',
      65: 'Lluvia intensa',
      66: 'Lluvia helada ligera',
      67: 'Lluvia helada intensa',
      71: 'Nieve ligera',
      73: 'Nieve moderada',
      75: 'Nieve intensa',
      77: 'Granizo',
      80: 'Chubascos ligeros',
      81: 'Chubascos moderados',
      82: 'Chubascos intensos',
      85: 'Chubascos de nieve ligeros',
      86: 'Chubascos de nieve intensos',
      95: 'Tormenta',
      96: 'Tormenta con granizo ligero',
      99: 'Tormenta con granizo intenso'
    };
    return descriptions[code] || `Código ${code}`;
  }

  private getWeatherIcon(code: number, isDay: boolean = true): string {
    const suffix = isDay ? 'd' : 'n';
    
    const icons: { [key: number]: string } = {
      0: `01${suffix}`, // Despejado
      1: `02${suffix}`, // Mayormente despejado
      2: `03${suffix}`, // Parcialmente nublado
      3: `04${suffix}`, // Nublado
      45: '50d', // Neblina
      48: '50d', // Niebla
      51: '09d', // Llovizna ligera
      53: '09d', // Llovizna moderada
      55: '09d', // Llovizna intensa
      56: '09d', // Llovizna helada
      57: '09d', // Llovizna helada intensa
      61: '10d', // Lluvia ligera
      63: '10d', // Lluvia moderada
      65: '10d', // Lluvia intensa
      66: '10d', // Lluvia helada
      67: '10d', // Lluvia helada intensa
      71: '13d', // Nieve ligera
      73: '13d', // Nieve moderada
      75: '13d', // Nieve intensa
      77: '13d', // Granizo
      80: '09d', // Chubascos ligeros
      81: '09d', // Chubascos moderados
      82: '09d', // Chubascos intensos
      85: '13d', // Chubascos de nieve
      86: '13d', // Chubascos de nieve intensos
      95: '11d', // Tormenta
      96: '11d', // Tormenta con granizo
      99: '11d'  // Tormenta con granizo intenso
    };
    
    return icons[code] || '03d';
  }
}
